import { useMemo, useState } from "react";
import axios from "axios";
import { X, AlertTriangle, CheckCircle2, Loader2, PenLine } from "lucide-react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { unitCompletionWarnings, derivedStatus } from "@/lib/unitStatus";
import SignatureCanvas from "@/components/SignatureCanvas";

const BACKEND = process.env.REACT_APP_BACKEND_URL;
const API_BASE = `${BACKEND}/api`;

/**
 * Validation dialog shown when the field worker presses
 * "Merkitse koko kohde valmiiksi".
 *
 * Behavior (per user spec):
 *   1. Display warnings for units that aren't yet done — advisory only.
 *   2. User can always proceed regardless of warnings.
 *   3. On confirm: bulk-mark remaining units to status="done" + trigger
 *      AI summary draft. User goes to summary tab to review/edit.
 *
 * Polymorphic across two auth modes:
 *   - Authenticated office user (default): uses `api` (Bearer token) + sets
 *     report.status="completed" for full closeout.
 *   - Field worker on /r-edit/{token}: pass `editToken` prop. Uses plain
 *     axios on edit-token endpoints. Skips the report-level status PATCH
 *     because that's an office-level decision.
 */
export default function MarkAllDoneDialog({ report, editToken, onClose, onCompleted }) {
  const isEditMode = !!editToken;
  const needsSignature = isEditMode && !!report.request_signature;
  const unitPatch = (unitId, body) => (isEditMode
    ? axios.patch(`${API_BASE}/reports/edit/${editToken}/units/${unitId}`, body)
    : api.patch(`/reports/${report.report_id}/units/${unitId}`, body));
  const reportPatch = (body) => api.patch(`/reports/${report.report_id}`, body); // auth-only
  const signaturePatch = (body) => axios.patch(`${API_BASE}/reports/edit/${editToken}/signature`, body);
  const triggerSummary = () => (isEditMode
    ? axios.post(`${API_BASE}/reports/edit/${editToken}/summary`)
    : api.post(`/reports/${report.report_id}/summary`));

  const [busy, setBusy] = useState(false);
  const [phase, setPhase] = useState("review"); // review | signing | finalizing
  const [sigData, setSigData] = useState(null);
  const [sigName, setSigName] = useState("");

  const issues = useMemo(() => {
    const out = [];
    for (const u of report.units || []) {
      const warns = unitCompletionWarnings(u);
      if (warns.length > 0) out.push({ unit: u, warnings: warns });
    }
    return out;
  }, [report.units]);

  const allDone = issues.length === 0;

  const confirm = async () => {
    // If signature is required and we haven't shown the signing step yet,
    // route to the signing phase first (the worker can still skip there).
    if (needsSignature && phase === "review") {
      setPhase("signing");
      return;
    }
    setBusy(true);
    setPhase("finalizing");
    try {
      // Persist signature first (if anything was drawn or name was entered)
      if (needsSignature && (sigData || sigName.trim())) {
        try {
          await signaturePatch({
            signature_data: sigData,
            signature_name: sigName.trim() || null,
          });
        } catch {
          // best-effort; don't block completion if signature save fails
        }
      }
      // 1. Bulk-mark all unfinished units to done (legacy "inaccessible" excluded)
      const toUpdate = (report.units || []).filter(
        (u) => derivedStatus(u) !== "done" && derivedStatus(u) !== "inaccessible",
      );
      for (const u of toUpdate) {
        await unitPatch(u.id, { status: "done" });
      }
      // 2. Set report status to completed — auth-mode only
      if (!isEditMode) {
        await reportPatch({ status: "completed" });
      }
      // 3. Trigger AI summary draft (best effort)
      let summaryGenerated = false;
      try {
        await triggerSummary();
        summaryGenerated = true;
      } catch {
        // ignore — user can re-trigger manually from the summary tab
      }
      toast.success(summaryGenerated ? "Kohde merkitty valmiiksi — yhteenvetoluonnos luotu" : "Kohde merkitty valmiiksi");
      onCompleted({ summaryGenerated });
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Valmistuksessa tapahtui virhe");
      setPhase("review");
    } finally {
      setBusy(false);
    }
  };

  const skipSignature = async () => {
    setSigData(null);
    setSigName("");
    // Re-enter confirm flow but bypass the signing route by directly running
    // the finalizing path.
    setBusy(true);
    setPhase("finalizing");
    try {
      const toUpdate = (report.units || []).filter(
        (u) => derivedStatus(u) !== "done" && derivedStatus(u) !== "inaccessible",
      );
      for (const u of toUpdate) {
        await unitPatch(u.id, { status: "done" });
      }
      if (!isEditMode) await reportPatch({ status: "completed" });
      let summaryGenerated = false;
      try {
        await triggerSummary();
        summaryGenerated = true;
      } catch { /* ignore */ }
      toast.success(summaryGenerated ? "Kohde merkitty valmiiksi — yhteenvetoluonnos luotu" : "Kohde merkitty valmiiksi");
      onCompleted({ summaryGenerated });
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Valmistuksessa tapahtui virhe");
      setPhase("signing");
    } finally {
      setBusy(false);
    }
  };

  if (phase === "signing") {
    return (
      <div className="fixed inset-0 z-50 flex items-end md:items-center justify-center p-0 md:p-6 bg-black/40" data-testid="signature-dialog">
        <div className="bg-white w-full md:max-w-lg rounded-t-2xl md:rounded-2xl shadow-xl max-h-[90vh] flex flex-col">
          <div className="flex items-center justify-between p-5 border-b border-slate-100">
            <div className="flex items-center gap-2.5">
              <PenLine className="h-6 w-6 text-emerald-600 flex-shrink-0" />
              <h3 className="font-display text-lg font-semibold text-slate-900">Allekirjoita raportti</h3>
            </div>
            <button onClick={onClose} className="p-2 rounded-lg hover:bg-slate-100" data-testid="signature-close">
              <X className="h-5 w-5 text-slate-400" />
            </button>
          </div>
          <div className="p-5 overflow-y-auto flex-1 space-y-4">
            <p className="text-sm text-slate-600">
              Allekirjoita raportti sormella tai hiirellä. Voit halutessasi myös ohittaa.
            </p>
            <SignatureCanvas onChange={setSigData} />
            <div>
              <label className="block text-xs uppercase tracking-wider text-slate-500 font-semibold mb-1.5">
                Nimenselvennys
              </label>
              <input
                type="text"
                value={sigName}
                onChange={(e) => setSigName(e.target.value)}
                placeholder="esim. Mikko Mallikas"
                className="w-full px-3 py-2.5 rounded-xl border border-slate-200 focus:border-emerald-400 outline-none text-sm"
                data-testid="signature-name-input"
              />
            </div>
          </div>
          <div className="p-5 border-t border-slate-100 flex flex-col-reverse sm:flex-row gap-2 justify-end">
            <button
              type="button"
              onClick={skipSignature}
              disabled={busy}
              className="px-4 py-2.5 rounded-lg border border-slate-200 text-slate-700 font-semibold hover:bg-slate-50 disabled:opacity-50"
              data-testid="signature-skip"
            >
              Ohita allekirjoitus
            </button>
            <button
              type="button"
              onClick={confirm}
              disabled={busy || (!sigData && !sigName.trim())}
              className="px-4 py-2.5 rounded-lg bg-emerald-600 text-white font-semibold hover:bg-emerald-700 disabled:opacity-50"
              data-testid="signature-confirm"
            >
              Tallenna ja viimeistele
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (phase === "finalizing") {
    const steps = [
      "Analysoidaan yksiköt",
      "Kootaan tehdyt toimenpiteet",
      "Kootaan havainnot",
      "Luodaan jatkotoimenpiteet",
    ];
    return (
      <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50" data-testid="mark-all-done-dialog">
        <div className="bg-white rounded-2xl shadow-2xl p-8 max-w-md w-full">
          <div className="flex items-center justify-center gap-3">
            <Loader2 className="h-8 w-8 text-emerald-600 animate-spin" />
            <div className="font-display text-lg font-semibold text-slate-900">🤖 Luodaan yhteenvetoa...</div>
          </div>
          <ul className="mt-6 space-y-2.5" data-testid="ai-summary-progress-steps">
            {steps.map((s, i) => (
              <li key={i} className="text-sm text-slate-700 flex items-center gap-2.5">
                <span
                  className="h-2 w-2 rounded-full bg-emerald-400 animate-pulse"
                  style={{ animationDelay: `${i * 200}ms` }}
                />
                {s}
              </li>
            ))}
          </ul>
          <div className="mt-6 text-xs text-slate-400 text-center">Tämä voi kestää hetken.</div>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 z-50 flex items-end md:items-center justify-center p-0 md:p-6 bg-black/40" data-testid="mark-all-done-dialog">
      <div className="bg-white w-full md:max-w-lg rounded-t-2xl md:rounded-2xl shadow-xl max-h-[90vh] flex flex-col">
        <div className="flex items-center justify-between p-5 border-b border-slate-100">
          <div className="flex items-center gap-2.5">
            {allDone ? (
              <CheckCircle2 className="h-6 w-6 text-emerald-600 flex-shrink-0" />
            ) : (
              <AlertTriangle className="h-6 w-6 text-amber-500 flex-shrink-0" />
            )}
            <h3 className="font-display text-lg font-semibold text-slate-900">
              {allDone ? "Kaikki yksiköt valmiina" : "Kohteessa on puutteita"}
            </h3>
          </div>
          <button onClick={onClose} className="p-2 rounded-lg hover:bg-slate-100" data-testid="mark-all-done-close">
            <X className="h-5 w-5 text-slate-400" />
          </button>
        </div>

        <div className="p-5 overflow-y-auto flex-1">
          {allDone ? (
            <div className="text-sm text-slate-700">
              Kaikki yksiköt on käsitelty. Merkataanko koko kohde valmiiksi ja luodaan yhteenvetoluonnos, jonka voit muokata?
            </div>
          ) : (
            <>
              <div className="text-sm text-slate-700 mb-4">
                Seuraavat yksiköt ovat keskeneräisiä. <strong>Voit silti merkitä kohteen valmiiksi</strong> — tämä on vain muistutus.
              </div>
              <div className="space-y-2.5 max-h-72 overflow-y-auto">
                {issues.map(({ unit, warnings }) => (
                  <div key={unit.id} className="rounded-xl border border-amber-200 bg-amber-50/50 p-3" data-testid={`issue-${unit.id}`}>
                    <div className="font-semibold text-sm text-slate-900">{unit.label}</div>
                    <ul className="mt-1 space-y-0.5">
                      {warnings.map((w, i) => (
                        <li key={i} className="text-xs text-amber-800 flex items-start gap-1.5">
                          <span className="text-amber-500 mt-0.5">•</span>
                          <span>{w}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                ))}
              </div>
              <div className="mt-4 rounded-xl bg-emerald-50 border border-emerald-100 p-3 text-xs text-emerald-900">
                💡 Vinkki: Tämän jälkeen pääset muokkaamaan yhteenvetoluonnosta ennen PDF:n luontia.
              </div>
            </>
          )}
        </div>

        <div className="p-5 border-t border-slate-100 flex flex-col-reverse sm:flex-row gap-2 justify-end">
          <button
            type="button"
            onClick={onClose}
            disabled={busy}
            className="px-4 py-2.5 rounded-lg border border-slate-200 text-slate-700 font-semibold hover:bg-slate-50 disabled:opacity-50"
            data-testid="mark-all-done-back"
          >
            {allDone ? "Peruuta" : "Palaa korjaamaan"}
          </button>
          <button
            type="button"
            onClick={confirm}
            disabled={busy}
            className="px-4 py-2.5 rounded-lg bg-emerald-600 text-white font-semibold hover:bg-emerald-700 disabled:opacity-50"
            data-testid="mark-all-done-confirm"
          >
            {allDone ? "Merkitse valmiiksi" : "Merkitse silti valmiiksi"}
          </button>
        </div>
      </div>
    </div>
  );
}
